// web/src/App.tsx
//
// BrowserRouter is provided by main.tsx — don't add another one here.
import { Navigate, Route, Routes } from "react-router-dom";
import { TradingView } from "./trading/TradingView";
import { SigninPage } from "./auth/SigninPage";
import { SignupPage } from "./auth/SignupPage";
import { ConfirmPage } from "./auth/ConfirmPage";
import { ResetPasswordPage } from "./auth/ResetPasswordPage";
import { RequireAuth } from "./auth/RequireAuth";
import { AppShell } from "./shell/AppShell";
import { CookieConsent } from "./shell/CookieConsent";
import { ErrorBoundary } from "./shell/ErrorBoundary";
import { MobileGate } from "./shell/MobileGate";
import { NotFoundPage } from "./shell/NotFoundPage";
import { initSentry } from "./observability/sentry";
import { ProfilePage } from "./profile/ProfilePage";
import { AccountPage } from "./account/AccountPage";
import { PreferencesPage } from "./preferences/PreferencesPage";
import { BillingSettings } from "./billing/BillingSettings";
import { useTheme } from "./preferences/useTheme";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
// CopilotProvider is mounted inside AppShell (Slice 2) so the Ask
// Tapelens chat is reachable from every authenticated route without
// needing a per-route wrapper.
import { DashboardWelcome } from "./onboarding/DashboardWelcome";
import { MonitorsListPage } from "./monitors/pages/MonitorsListPage";
import { MonitorWizardPage } from "./monitors/pages/MonitorWizardPage";
import { MonitorDetailPage } from "./monitors/pages/MonitorDetailPage";
import { MatchesInboxPage } from "./monitors/pages/MatchesInboxPage";
import { ClusterDetailPage } from "./monitors/pages/ClusterDetailPage";
import { CalendarPage } from "./calendar/CalendarPage";
import { NotesPage } from "./notes/NotesPage";
import { LanguageProvider } from "./i18n/LanguageProvider";
import { useCurrentUser } from "./shell/useCurrentUser";
import { useTranslation } from "react-i18next";

/**
 * NotFoundPage exposes `homeLabel` as a prop so the marketing build can
 * reuse it. In the app build we want the label to be locale-aware; this
 * small wrapper pulls the translation here so the parent NotFoundPage
 * stays presentational and reusable.
 */
function AppNotFoundPage() {
  const { t } = useTranslation("common");
  return <NotFoundPage homeTo="/dashboard" homeLabel={t("notFound.backToDashboard")} />;
}

/**
 * Wraps authenticated routes so the user's preferred language (sourced
 * from /me) drives i18next + <html lang/dir>. Public auth routes use a
 * LanguageProvider without `mePreferredLanguage`, so they fall back to
 * localStorage/navigator.
 */
function AuthenticatedLanguageProvider({ children }: { children: React.ReactNode }) {
  const { data: me } = useCurrentUser();
  return (
    <LanguageProvider mePreferredLanguage={me?.preferred_language}>
      {children}
    </LanguageProvider>
  );
}

export function App() {
  // Mount the theme hook once at the app root so it toggles the .light class on
  // <html> on first render and listens for system/cross-tab changes for the
  // lifetime of the SPA. The inline FOUC-prevention script in index.html sets
  // the initial class before React mounts.
  useTheme();

  return (
    <TooltipProvider delayDuration={300}>
      <ErrorBoundary>
        <Routes>
          {/* Public auth routes (no shell). LanguageProvider here resolves
              from localStorage/navigator only — there's no `me` yet. */}
          <Route
            path="/auth/signin"
            element={
              <LanguageProvider>
                <SigninPage />
              </LanguageProvider>
            }
          />
          <Route
            path="/auth/signup"
            element={
              <LanguageProvider>
                <SignupPage />
              </LanguageProvider>
            }
          />
          <Route
            path="/auth/confirm"
            element={
              <LanguageProvider>
                <ConfirmPage />
              </LanguageProvider>
            }
          />
          <Route
            path="/auth/reset-password"
            element={
              <LanguageProvider>
                <ResetPasswordPage />
              </LanguageProvider>
            }
          />

          {/* Backwards-compat: old /settings/account → /account */}
          <Route path="/settings/account" element={<Navigate to="/account" replace />} />

          {/* Authenticated app shell wraps everything below.
              CopilotProvider lives INSIDE AppShell (Slice 2) so the
              Ask Tapelens chat is reachable from every authenticated
              route — not just /dashboard. */}
          <Route
            element={
              <RequireAuth>
                <AuthenticatedLanguageProvider>
                  <AppShell />
                </AuthenticatedLanguageProvider>
              </RequireAuth>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route
              path="/dashboard"
              element={
                <MobileGate>
                  <ErrorBoundary scope="Trading dashboard">
                    <TradingView />
                    <DashboardWelcome />
                  </ErrorBoundary>
                </MobileGate>
              }
            />
            <Route
              path="/monitors/new"
              element={
                <MobileGate>
                  <ErrorBoundary scope="Monitor wizard">
                    <MonitorWizardPage />
                  </ErrorBoundary>
                </MobileGate>
              }
            />
            <Route
              path="/monitors/matches"
              element={
                <MobileGate>
                  <ErrorBoundary scope="Matches inbox">
                    <MatchesInboxPage />
                  </ErrorBoundary>
                </MobileGate>
              }
            />
            <Route
              path="/monitors"
              element={
                <MobileGate>
                  <ErrorBoundary scope="Monitors list">
                    <MonitorsListPage />
                  </ErrorBoundary>
                </MobileGate>
              }
            />
            {/* NOTE: literal `/monitors/new` and `/monitors/matches` are
                declared above this `:id` pattern so they win on collision —
                otherwise "new" / "matches" would be captured as :id. */}
            <Route
              path="/monitors/:id"
              element={
                <MobileGate>
                  <ErrorBoundary scope="Monitor detail">
                    <MonitorDetailPage />
                  </ErrorBoundary>
                </MobileGate>
              }
            />
            <Route
              path="/clusters/:cluster_id"
              element={
                <MobileGate>
                  <ErrorBoundary scope="Cluster detail">
                    <ClusterDetailPage />
                  </ErrorBoundary>
                </MobileGate>
              }
            />
            <Route
              path="/calendar"
              element={
                <MobileGate>
                  <ErrorBoundary scope="World Calendar">
                    <CalendarPage />
                  </ErrorBoundary>
                </MobileGate>
              }
            />
            <Route
              path="/notes"
              element={
                <MobileGate>
                  <ErrorBoundary scope="Notes">
                    <NotesPage />
                  </ErrorBoundary>
                </MobileGate>
              }
            />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/preferences" element={<PreferencesPage />} />
            <Route path="/account/billing" element={<BillingSettings />} />
            {/* Catch-all inside the shell → 404 with link back to dashboard */}
            <Route
              path="*"
              element={<AppNotFoundPage />}
            />
          </Route>
        </Routes>
        <Toaster />
        <CookieConsent
          onChange={(value) => {
            if (value === "accepted") initSentry();
          }}
        />
      </ErrorBoundary>
    </TooltipProvider>
  );
}
